import { getI18n } from "react-i18next";
import { BookingStatusType } from "../types/baseTypes";
import { Reservation } from "../types/order";
import { Rental } from "../types/rental";
import i18nInstance from "i18next";
import {BookingSummary as BookingSummaryProps} from "../types/bookingCreation";
import moment from "moment/moment";

export function convertMinutesToHours(minutes: number, fullLabel = false): string {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  let translatedHourLabel = ""
  if (fullLabel) {
    translatedHourLabel = getI18n().t(
        "book_location.confirmation.hours_full_string",
        {count: hours}
    )
  } else {
    translatedHourLabel = getI18n().t(
        "book_location.confirmation.hours"
    )
  }
  const translatedMinutesLabel = getI18n().t("book_location.confirmation.minutes");

  if (hours > 0 && remainingMinutes > 0) {
    return `${hours} ${translatedHourLabel} ${remainingMinutes} ${translatedMinutesLabel}`;
  } else if (hours > 0) {
    return `${hours} ${translatedHourLabel}`;
  } else {
    return `${remainingMinutes} ${translatedMinutesLabel}`;
  }
}
type OrderItem = { id: string };

export function getIdString(items: OrderItem[]): string {
  return items?.map((item) => item?.id)?.join(",");
}

export const isEmptyObject = (obj: object): boolean => {
  return Object.keys(obj).length === 0;
};

export const getBookingStatusType = (rental: Rental, rentalExpired: boolean, serviceType: string): BookingStatusType => {
  if (!rental || isEmptyObject(rental)) {
    if(serviceType === "venue"){
      return "pending";
    }else{
      return "booked";
    }
  }

  // Consider both camelCase and snake_case finish fields
  const hasFinishedDate = Boolean((rental as any).finishedAtDate || (rental as any).finished_at);

  if (rentalExpired || (!rental.isActive && hasFinishedDate)) {
    return "finished";
  }

  if (rental && ["item_retrieved_confirmed"].includes(rental.state ?? "")) {
    return "finished";
  }

  if (rental.isActive) {
    return "active";
  }

  if (!rental.isActive && !hasFinishedDate) {
    return "canceled";
  }

  // Default case (though all cases should be covered above)
  return "finished";
};

export const checkIfOrderCanBeStarted = (
    order: BookingSummaryProps,
    gracePeriodMinutes: number = 0
) => {
  const currentLocationTime = new Date();
  const startDate = moment(order.items[0].fromDate);
  const endDate = moment(order.items[0].toDate);
  const endDateWithGracePeriod = moment(endDate).add(gracePeriodMinutes, "minutes");
  const locationDate = moment(currentLocationTime);
  return locationDate.isSameOrAfter(startDate) && locationDate.isSameOrBefore(endDateWithGracePeriod);
};

export function formatDateTime(input: string) {
  // Split the input into the date and the time portion
  const lastSpaceIndex = input.lastIndexOf(" "); // Find the last space before 'am' or 'pm'

  // Extract the date and time parts
  const date = input.slice(0, lastSpaceIndex - 5); // The date portion (subtracting 5 to exclude the time)
  const time = input.slice(lastSpaceIndex - 5); // The time portion, including am/pm

  // Return the formatted string with a vertical bar
  return `${date} | ${time}`;
}

export function transformReservations(reservations: Reservation[]): { id: string }[] {
  return reservations?.map((reservation) => ({ id: reservation?.id }));
}

/**
 * Helper to format currency dependending on the locale.
 * Remarks: Change format for english locale to 'en-US' if needed.
 */
export const formatCurrency = (
  value: number,
  locale: string,
  defaultCurrency = "EUR"
): string => {
  const tenantConfig = (window as any)?.tenantConfig;
  const tenantName = getTenantName() ?? "";

  const resolvedCurrency =
    tenantConfig?.currency ??
    (tenantName === "solheimsviken" ? "NOK" : defaultCurrency);

  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: resolvedCurrency,
    currencyDisplay: "symbol", // Show only symbol, not code like "NOK"
  });

  let formatted = formatter.format(value);

  // Remove unwanted ISO code like "NOK" if it's still there
  const currencyCodePattern = new RegExp(`\\b${resolvedCurrency}\\b`, "g");
  formatted = formatted.replace(currencyCodePattern, "").trim();

  // Ensure correct symbol position (after number)
  if (["NOK", "EUR"].includes(resolvedCurrency)) {
    const symbol = resolvedCurrency === "NOK" ? "kr" : "€";
    formatted = formatted.replace(symbol, "").trim();
    formatted = `${formatted} ${symbol}`;
  }

  return formatted;
};

export const getEquipmentName = (
  equipmentName: string,
  equipmentLangNames?: Record<string, { title: string; description: string }>,
  returnType: "title" | "description" = "title"
) => {
  const lang = i18nInstance.language;

  if (!equipmentLangNames) {
    return equipmentName;
  }

  if (Object.keys(equipmentLangNames).includes(lang)) {
    if (Object.keys(equipmentLangNames[lang]).includes(returnType)) {
      return equipmentLangNames[lang][returnType];
    }
  }
  return equipmentName;
};


export const getTenantName = () => {
  const environment = process.env.REACT_APP_ENV ?? "integration";
  const isDev = ["integration", "development"].includes(environment);
  const envTenantName = process.env.REACT_APP_TENANT;
  return isDev ? (localStorage.getItem("tenant") ?? envTenantName) : envTenantName;
}


// interface OpeningHours {
//   [key: string]: [number, number]; // [openHour, closeHour]
// }

// const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

// const openingHours: OpeningHours = {
//   mon: [8, 15],
//   tue: [8, 15],
//   wed: [8, 15],
//   thu: [8, 15],
//   fri: [8, 15],
//   sat: [8, 15],
//   sun: [8, 15],
// };

// const OPENING_TIME_INTERVAL = 0; // minutes

// const getDayOfWeek = (date: Date): number => {
//   return date.getDay(); // Sunday = 0, Monday = 1, etc.
// };

// const getDayKey = (date: Date): string => {
//   return DAYS[getDayOfWeek(date)];
// };

// const getModifiedOpeningHour = (
//     openHour: number,
//     date: Date,
//     type?: 'startingEnd' | 'startingStart' | 'endingStart' | 'endingEnd',
//     isSameDay?: boolean
// ) => {
//   let modifiedOpeningHour = openHour;
//   if (date.toISOString().slice(0, 10) === new Date().toISOString().slice(0, 10)) {
//     modifiedOpeningHour = Math.max(modifiedOpeningHour, new Date().getHours() + 1);
//   }

//   if (isSameDay) {
//     if (type === 'endingStart') {
//       modifiedOpeningHour += 1;
//     }
//   }

//   if (type === 'startingEnd') {
//     modifiedOpeningHour -= 1;
//   }

//   return modifiedOpeningHour;
// };

// const generateTimeSlots = (
//     startHour: number,
//     endHour: number,
// ): string[] => {
//   const slots: string[] = [];

//   if (startHour === endHour) {
//     return slots;
//   }

//   const startMinute = 0;

//   for (let hour = startHour; hour <= endHour; hour++) {
//     // Handle 0 interval case
//     if (OPENING_TIME_INTERVAL === 0) {
//       slots.push(`${hour.toString().padStart(2, '0')}:00`);
//     } else {
//       for (let minute = hour === startHour ? startMinute : 0; minute < 60; minute += OPENING_TIME_INTERVAL) {
//         if (hour === endHour && minute > 0) continue;
//         slots.push(
//             `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
//         );
//       }
//     }
//   }

//   return slots;
// };

// const getStartTimes = (
//     date: Date,
//     isSameDay: boolean
// ): string[] => {
//   const dayKey = getDayKey(date);
//   const [openHour, closeHour] = openingHours[dayKey];

//   const modifiedOpeningHour = getModifiedOpeningHour(openHour, date, 'startingStart', isSameDay);
//   const modifiedClosingHour = getModifiedOpeningHour(closeHour, date, 'startingEnd', isSameDay);

//   return generateTimeSlots(modifiedOpeningHour, modifiedClosingHour);
// };

// const getEndTimes = (
//     date: Date,
//     isSameDay: boolean
// ): string[] => {
//   const dayKey = getDayKey(date);
//   const [openHour, closeHour] = openingHours[dayKey];
//   let slots: string[] = [];

//   const modifiedOpeningHour = getModifiedOpeningHour(openHour, date, 'endingStart', isSameDay);
//   const modifiedClosingHour = getModifiedOpeningHour(closeHour, date, 'endingEnd', isSameDay);

//   // Special handling for overnight stays (if applicable)
//   slots = generateTimeSlots(modifiedOpeningHour, modifiedClosingHour);

//   return slots;
// };

// export const getAvailableBookingTimes = (
//     checkinDate: string,
//     checkoutDate: string,
// ) => {
//   // Parse input dates
//   const checkin = new Date(checkinDate);
//   const checkout = new Date(checkoutDate);

//   // Validate dates
//   if (checkin > checkout) {
//     throw new Error('Check-in date must be before check-out date');
//   }

//   const isSameDay = checkin.toISOString().slice(0, 10) === checkout.toISOString().slice(0, 10);

//   // Get available times for check-in and check-out dates
//   const availableStartTimes = getStartTimes(checkin, isSameDay);
//   const availableEndTimes = getEndTimes(checkout, isSameDay);

//   return {
//     availableStartTimes,
//     availableEndTimes,
//   };
// };

type OpeningHours = {
  [key: string]: [number, number]; // [startHour, endHour]
};

const openingHours: OpeningHours = {
  mon: [8, 15],
  tue: [8, 15],
  wed: [8, 15],
  thu: [8, 15],
  fri: [8, 15],
  sat: [8, 15],
  sun: [8, 15],
};

function getDayKey(date: Date): keyof OpeningHours {
  const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  return days[date.getDay()] as keyof OpeningHours;
}

function generateHourSlots(start: number, end: number): string[] {
  const slots: string[] = [];
  for (let h = start; h < end; h++) {
    slots.push(`${h}:00`);
  }
  return slots;
}

export function getAvailableBookingTimes(
  startDateIso: string,
  endDateIso: string,
  selectedStartTime?: string, // e.g. "9:00"
  selectedEndTime?: string // not used yet, but kept for flexibility
): { availableStartTimes: string[]; availableEndTimes: string[] } {
  const startDate = new Date(startDateIso);
  const endDate = new Date(endDateIso);
  const now = new Date();

  const startDayKey = getDayKey(startDate);
  const endDayKey = getDayKey(endDate);

  const [startOpen, startClose] = openingHours[startDayKey];
  const [endOpen, endClose] = openingHours[endDayKey];

  let availableStartTimes: string[] = [];
  let availableEndTimes: string[] = [];

  // ---- Start Times ----
  if (startDate.toDateString() === now.toDateString()) {
    // today → only future slots
    const nextHour = now.getHours() + 1;
    if (nextHour < startClose) {
      availableStartTimes = generateHourSlots(
        Math.max(startOpen, nextHour),
        startClose
      );
    }
  } else {
    availableStartTimes = generateHourSlots(startOpen, startClose);
  }

  // ---- End Times ----
  console.log('selectedStartTime', selectedStartTime);
  if (selectedStartTime === "0") {
    // no start time chosen yet → empty array
    availableEndTimes = [];
  } else if (selectedStartTime && startDate.toDateString() === endDate.toDateString()) {
    // Same day checkout
    const startHour = parseInt(selectedStartTime.split(":")[0]);

    if (startHour + 1 <= endClose) {
      availableEndTimes = generateHourSlots(
        Math.max(startHour + 1, endOpen),
        endClose + 1
      );
    }
  } else {
    // Different day checkout → full slots on checkout day
    availableEndTimes = generateHourSlots(endOpen, endClose + 1);
  }

  return { availableStartTimes, availableEndTimes };
}




export const eventTypes = {
  "en": ["Seminar", "Birthday", "Wedding", "Other"],
  "de": ["Seminar", "Geburtstag", "Hochzeit", "Andere"],
}

type Language = "en" | "de";

export function getEventType(eventType: string, language: Language): string {
  // Find the index in English array (default if not found)
  const enIndex = eventTypes.en.indexOf(eventType as typeof eventTypes.en[number]);

  // Find the index in German array (fallback if not found in English)
  const deIndex = eventTypes.de.indexOf(eventType as typeof eventTypes.de[number]);

  // If found in English array, return corresponding German term (or English if language is English)
  if (enIndex !== -1) {
    return eventTypes[language][enIndex];
  }

  // If found in German array, return corresponding English term (or German if language is German)
  if (deIndex !== -1) {
    return eventTypes[language][deIndex];
  }

  // If not found in either, return the original string
  return eventType;
}

export function setTimeOnDate(isoDateString: string, timeString: string): string {
  // Parse the original date
  const date = new Date(isoDateString);

  // Extract hours and minutes from time string
  const [hours, minutes] = timeString.split(':').map(Number);

  // Validate time input
  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    throw new Error('Invalid time format. Expected HH:mm with hours 0-23 and minutes 0-59');
  }

  // Create a new date with the original date but new time
  const newDate = new Date(date);
  newDate.setUTCHours(hours, minutes, 0, 0);

  // Return as ISO string
  return newDate.toISOString();
}

export function setToNoon(dateString: string, isStartDate:boolean): string {
  // Parse the input date string
  const date = new Date(dateString);

  if(isStartDate){
  // Set the time to 12:00:00.000 (noon)
  date.setHours(12, 0, 0, 0);
  
  }else{
  // Set the time to 12:00:00.000 (noon)
  date.setHours(11, 59, 0, 0);
  
  }
  // Return in ISO format (same format as input)
  return date.toISOString();
}